// RegisterComponent.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18N, useZoomDetection, useModalHeight } from "common/hooks";
import {
  publicPrivacyUrl,
  publicTermsUrl,
  termsAndConditionsAgreedKey,
} from "common/config";
import Checkbox from "@/components/common/Checkbox";
import WideButton from "@/components/common/WideButton";

interface VerifyTermsAndConditionsProps {
  onClose: () => void;
  title: string;
}

const VerifyTermsAndConditions = ({
  onClose,
  title,
}: VerifyTermsAndConditionsProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { modalStyle, modalClassName } = useModalHeight();

  const onContinue = useCallback(async () => {
    const timestamp = new Date().getTime();
    localStorage.setItem(
      termsAndConditionsAgreedKey,
      JSON.stringify({ accepted: true, timestamp }),
    );

    //close popup
    onClose();
  }, [onClose]);

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={modalStyle}
      className="relative bg-bg-brand-secondary-default rounded-xl h-full flex flex-col overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {title}
        </h1>
      </header>

      <div
        aria-label="Modal Content"
        className="flex-1 py-3 px-6 md:py-6 md:px-14 overflow-y-auto"
        role="none"
      >
        {/* content comes below this */}
        <h2 className="title-m-bold text-fg-neutral-strong my-[16px]">
          {i18n.get("beforeYouUseSociability")}
        </h2>
        <div className="my-0 md:my-2 text-fg-neutral-strong">
          {i18n.get("byClickingAgree")}
        </div>
        <div className="pb-5">
          <hr />
        </div>

        <div className="flex flex-row items-start">
          <div className="mt-1 mr-2">
            <Checkbox
              label=""
              checked={termsAccepted}
              onCheck={(value) => {
                setTermsAccepted(value);
              }}
            />
          </div>
          <div className="">
            <span className="text-fg-neutral-strong label-m-medium mr-1">
              {i18n.get("bySelectingAgreeAndContinue")}
            </span>
            <a
              href={publicTermsUrl}
              target="_blank"
              className="label-m-medium leading-normal mr-1 underline"
            >
              {i18n.get("termsAndConditions")}
            </a>
            <span className=" label-m-medium leading-normal mr-1">
              {i18n.get("and")}
            </span>
            <a
              href={publicPrivacyUrl}
              target="_blank"
              className="label-m-medium leading-normal underline"
            >
              {i18n.get("privacyNotice")}
            </a>
          </div>
        </div>

        <div className="flex justify-center mb-4 mt-8">
          <WideButton disabled={!termsAccepted} onClick={() => onContinue()}>
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>
    </section>
  );
};

export default VerifyTermsAndConditions;
